import { useMemo, type ReactNode } from 'react';
import { BrandingProvider } from '@sentinel/ui';
import { useBrandingQuery } from './use-branding';

export interface BrandingLoaderProps {
  /**
   * Explicit tenant id override. When omitted, BrandingLoader looks for a
   * `?tenant=<uuid>` query parameter on the current URL. This is a hint —
   * unauthenticated users hitting `/login` with no tenant context fall back
   * to the default branding.
   */
  tenantId?: string;
  children: ReactNode;
}

// RFC 4122 UUID v4/v7 shape — defence in depth so a hostile `?tenant=…`
// query value cannot inject arbitrary text into a downstream URL query.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function readTenantHintFromUrl(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('tenant');
  if (!raw) return undefined;
  return UUID_RE.test(raw) ? raw : undefined;
}

export function BrandingLoader({ tenantId, children }: BrandingLoaderProps) {
  // TODO(branding): in addition to ?tenant=<uuid>, we should also resolve
  // tenant by slug (e.g. `?tenant=acme`) and from a `sentinel_tenant_hint`
  // cookie set by the backend after the first successful authentication.
  // Neither lookup currently exists on the backend: `GET /auth/branding`
  // only accepts a `tenant_id` UUID, and no `sentinel_tenant_hint` cookie
  // is set anywhere in `crates/`. Track follow-up in the branding epic.
  const hintFromUrl = useMemo(readTenantHintFromUrl, []);
  const effectiveTenantId = tenantId ?? hintFromUrl;
  const { data } = useBrandingQuery(effectiveTenantId);
  return <BrandingProvider branding={data}>{children}</BrandingProvider>;
}
